import {ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, TemplateRef} from '@angular/core';
import {VersionCheckService} from "../../../core/versions/version-check.service";
import {NavigationHistoryService} from '../../../core/service/navigation-history.service';
import {ModalService} from '../../../shared/modal';
import {UpdateConfirmationModalComponent} from "../../../shared/components/update-confirmation-modal/update-confirmation-modal.component";
import {Router} from '@angular/router';
import {NgTemplateOutlet} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';
import {Subscription} from "rxjs";
import {ChatTopbarContext, LayoutService, MobileRightPanelConfig, TopbarBackButton} from "../layout.service";
import {RightPanelService} from '../right-panel/right-panel.service';
import {ChatActionsDropdownComponent} from '../../../apps/chats/list/chat-container/chat-actions-dropdown/chat-actions-dropdown.component';
import {ToastType} from "../../../shared/components/toast/toast.model";
import {ToastService} from "../../../shared/service/toast.service";
import {EventService} from "../../../core/service/event.service";
import {TourService} from "../../../core/service/tour.service";
import {IconComponent} from "../../../shared/components/icon/icon.component";
import {UsageChipComponent} from '../../../shared/subscription-overview/usage-chip.component';
import {MENU_ITEMS, MenuItem} from '../models/menu.model';

/** Cesty dosažitelné jedním klikem z levého menu — na nich šipka zpět nemá smysl. */
const MENU_ROOT_PATHS: ReadonlySet<string> = new Set(
    (function collect(items: MenuItem[]): string[] {
        return items.flatMap(i => [...(i.link ? [i.link] : []), ...collect(i.children ?? [])]);
    })(MENU_ITEMS)
);

@Component({
    selector: 'app-topbar',
    templateUrl: './topbar.component.html',
    styleUrls: ['./topbar.component.scss'],
    imports: [TranslatePipe, ChatActionsDropdownComponent, IconComponent, NgTemplateOutlet, UsageChipComponent]
})
export class TopbarComponent implements OnInit, OnDestroy {

    // Update
    updateAvailable = false;
    checkingVersion = false;
    showUpdateCountdown = false;
    updateMinimized = false;
    updateCountdownDisplay = '';
    private versionSubscription: Subscription;
    private updateTimer: {main: ReturnType<typeof setTimeout>; warning: ReturnType<typeof setTimeout> | null} | null = null;
    private displayTimerInterval: ReturnType<typeof setInterval> | null = null;
    private readonly UPDATE_PENDING_KEY = 'update_pending_timestamp';
    private readonly UPDATE_DELAY_MS = 5 * 60 * 1000; // 5 minutes

    // Sidebar state
    isSidebarCollapsed = false;
    private sidebarCollapsedSubscription?: Subscription;

    // Page title + chat context
    pageTitle = '';
    chatCtx: ChatTopbarContext | null = null;
    backBtn: TopbarBackButton | null = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    topbarContentRight: TemplateRef<any> | null = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    topbarContentLeft: TemplateRef<any> | null = null;
    private pageTitleSubscription?: Subscription;
    private chatCtxSubscription?: Subscription;
    private backBtnSubscription?: Subscription;
    private topbarContentRightSubscription?: Subscription;
    private topbarContentLeftSubscription?: Subscription;

    // Right panel toggle (3-column layouts)
    rightPanel: MobileRightPanelConfig | null = null;
    private rightPanelToggleSubscription?: Subscription;

    constructor(
        private readonly versionCheck: VersionCheckService,
        private readonly zone: NgZone,
        private readonly cdr: ChangeDetectorRef,
        private readonly modalService: ModalService,
        private readonly toastService: ToastService,
        private readonly router: Router,
        private readonly eventService: EventService,
        private readonly tourService: TourService,
        private readonly layoutService: LayoutService,
        protected readonly rightPanelService: RightPanelService,
        protected readonly navHistory: NavigationHistoryService,
    ) {}

    /**
     * Univerzální šipka zpět jen na zanořených obrazovkách — tj. těch, na které nevede přímý
     * odkaz z levého menu (porovnává se cesta bez query, takže i výpis subjektů s jiným `type`
     * je kořenová obrazovka). Stránky s vlastním setTopbarBack mají přednost.
     */
    get showDefaultBack(): boolean {
        const path = this.navHistory.currentUrl().split(/[?#]/)[0];
        return path.startsWith('/page/') && !MENU_ROOT_PATHS.has(path);
    }

    ngOnInit(): void {
        this.chatCtxSubscription = this.layoutService.chatTopbarContext$.subscribe(ctx => this.chatCtx = ctx);
        this.backBtnSubscription = this.layoutService.topbarBack$.subscribe(btn => this.backBtn = btn);
        this.topbarContentRightSubscription = this.layoutService.topbarContentRight$.subscribe(tpl => this.topbarContentRight = tpl);
        this.topbarContentLeftSubscription = this.layoutService.topbarContentLeft$.subscribe(tpl => this.topbarContentLeft = tpl);
        this.rightPanelToggleSubscription = this.layoutService.mobileRightPanelToggle$.subscribe(cfg => this.rightPanel = cfg);
        this.checkPendingUpdate();
        this.sidebarCollapsedSubscription = this.layoutService.sidebarCollapsed$.subscribe(v => this.isSidebarCollapsed = v);
        this.pageTitleSubscription = this.layoutService.pageTitle$.subscribe(title => this.pageTitle = title);

        this.versionSubscription = this.versionCheck.updateAvailable$.subscribe(available => {
            this.zone.run(() => {
                this.checkingVersion = false;
                this.updateAvailable = available;
                this.cdr.detectChanges();
                if (available) this.openUpdateConfirmDialog();
            });
        });
    }

    ngOnDestroy(): void {
        this.versionSubscription?.unsubscribe();
        this.sidebarCollapsedSubscription?.unsubscribe();
        this.pageTitleSubscription?.unsubscribe();
        this.chatCtxSubscription?.unsubscribe();
        this.backBtnSubscription?.unsubscribe();
        this.topbarContentRightSubscription?.unsubscribe();
        this.topbarContentLeftSubscription?.unsubscribe();
        this.rightPanelToggleSubscription?.unsubscribe();
        this.clearUpdateTimer();
        this.clearDisplayTimer();
    }

    // ─── Update ──────────────────────────────────────────────────────────────────

    private checkPendingUpdate(): void {
        const timestampStr = localStorage.getItem(this.UPDATE_PENDING_KEY);
        if (!timestampStr) return;
        const elapsed = Date.now() - parseInt(timestampStr, 10);
        const remaining = this.UPDATE_DELAY_MS - elapsed;
        this.updateAvailable = true;
        if (remaining <= 0) {
            this.performUpdate();
        } else {
            this.startUpdateTimer(remaining);
        }
    }

    private startUpdateTimer(delay: number = this.UPDATE_DELAY_MS): void {
        this.clearUpdateTimer();
        this.clearDisplayTimer();
        this.updateAvailable = true;

        const endTime = Date.now() + delay;
        this.showUpdateCountdown = true;
        this.updateCountdownDisplay = this.formatCountdown(delay);

        this.displayTimerInterval = setInterval(() => {
            const remaining = endTime - Date.now();
            if (remaining <= 0) {
                this.clearDisplayTimer();
            } else {
                this.updateCountdownDisplay = this.formatCountdown(remaining);
            }
        }, 1000);

        let warningTimer: ReturnType<typeof setTimeout> | null = null;
        if (delay > 60000) {
            warningTimer = setTimeout(() => {
                this.toastService.broadcast(ToastType.TOAST, {
                    level: 'warning',
                    title: 'update.automatic.notice.title',
                    content: 'update.automatic.notice.description'
                });
            }, delay - 60000);
        }

        const mainTimer = setTimeout(() => this.performUpdate(), delay);
        this.updateTimer = {main: mainTimer, warning: warningTimer};
    }

    private formatCountdown(ms: number): string {
        const totalSeconds = Math.ceil(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    private clearUpdateTimer(): void {
        if (this.updateTimer) {
            clearTimeout(this.updateTimer.main);
            if (this.updateTimer.warning) clearTimeout(this.updateTimer.warning);
            this.updateTimer = null;
        }
    }

    private clearDisplayTimer(): void {
        if (this.displayTimerInterval) {
            clearInterval(this.displayTimerInterval);
            this.displayTimerInterval = null;
        }
        this.showUpdateCountdown = false;
        this.updateCountdownDisplay = '';
    }

    /**
     * Schedules (or keeps) the pending auto-update countdown after the user
     * dismisses the confirmation dialog. The original deadline is preserved on
     * repeated cancels — reopening the dialog and cancelling again must NOT
     * reset the timer back to the full delay.
     */
    private schedulePendingUpdate(): void {
        // A countdown is already running — leave it untouched.
        if (this.updateTimer) return;

        const timestampStr = localStorage.getItem(this.UPDATE_PENDING_KEY);
        if (timestampStr) {
            const remaining = this.UPDATE_DELAY_MS - (Date.now() - parseInt(timestampStr, 10));
            if (remaining > 0) {
                this.startUpdateTimer(remaining);
                return;
            }
            // Stored deadline already elapsed — fall through to apply immediately.
            this.performUpdate();
            return;
        }

        // First dismissal — record the deadline and start the full countdown.
        localStorage.setItem(this.UPDATE_PENDING_KEY, Date.now().toString());
        this.startUpdateTimer();
    }

    private performUpdate(): void {
        this.clearUpdateTimer();
        this.clearDisplayTimer();
        localStorage.removeItem(this.UPDATE_PENDING_KEY);
        this.versionCheck.applyUpdate();
    }

    /** Floating notification "Refresh now" — apply the update immediately, no dialog. */
    updateNow(): void {
        this.performUpdate();
    }

    openUpdateConfirmDialog(): void {
        if (this.updateAvailable) {
            const modalRef = this.modalService.open(UpdateConfirmationModalComponent, {
                size: 'md', centered: true, backdrop: 'static', keyboard: false
            });
            modalRef.result.then(
                (result) => {
                    if (result === 'confirm') {
                        this.performUpdate();
                    } else {
                        this.schedulePendingUpdate();
                    }
                },
                () => this.schedulePendingUpdate()
            );
        } else if (!this.checkingVersion) {
            this.checkingVersion = true;
            this.versionCheck.performCheck();
        }
    }

    // ─── Tour ────────────────────────────────────────────────────────────────────

    startDashboardTour(): void {
        const url = this.router.url;
        this.tourService.cancel();

        if (url.includes('/page/dashboard')) {
            this.eventService.broadcast('start-dashboard-tour');
        } else if (url.includes('/page/papers/editor/air')) {
            this.eventService.broadcast('start-create-air-dialog-tour');
            setTimeout(() => { if (!this.tourService.isActive()) this.showDefaultTour(); }, 200);
        } else if (url.includes('/page/papers/air') || url.includes('/page/papers/core')) {
            this.eventService.broadcast('start-paper-list-tour');
        } else if (url.includes('/page/images/editor')) {
            this.eventService.broadcast('start-current-tour');
        } else if (url.includes('/page/images')) {
            this.eventService.broadcast('start-image-gallery-tour');
        } else if (url.includes('/page/translator')) {
            this.eventService.broadcast('start-translator-tour');
        } else if (url.includes('/page/chats')) {
            this.eventService.broadcast('start-chat-tour');
        } else if (url.includes('/page/contexts')) {
            this.eventService.broadcast('start-contexts-list-tour');
        } else {
            this.showDefaultTour();
        }
    }

    private showDefaultTour(): void {
        // shepherd.js se stahuje až tady (líně přes TourService), ne při startu aplikace.
        void this.tourService.startSteps([{
            id: 'no-tour-available',
            title: 'Průvodce není dostupný',
            text: `<div style="text-align:center;padding:10px 0"><div style="font-size:48px;margin-bottom:15px">🚧</div><p style="line-height:1.6;margin:0">Na interaktivní nápovědě k této části aplikace intenzivně pracujeme. Brzy bude k dispozici!</p></div>`,
            buttons: [{text: 'Zavřít', action: () => this.tourService.complete(), classes: 'shepherd-button-primary'}]
        }], {scrollTo: false});
    }

    // ─── Sidebar ─────────────────────────────────────────────────────────────────

    toggleSidebar(): void {
        this.layoutService.toggleSidebar();
    }
}
